import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { type SharedEventLoop } from '../../event-loop';
import { type SelectionInput } from '../../types';
import { type TableNavigationAdapter } from '../table-navigation-adapter';

import { createTableTabNavigationKeyDownHandler } from './handle-table-tab-navigation-key-down';

/**
 * Creates the table-specific Tab keyboard input.
 *
 * This input subscribes to the shared synchronous keyboard loop so Tab can be
 * captured before browser focus moves into legacy table controls. It delegates
 * table geometry to the adapter instead of importing frontend table layout code
 * into the selections package.
 */
export function createTableTabNavigationInput(
  machine: SelectionsMachine,
  sharedLoop: SharedEventLoop,
  adapter: TableNavigationAdapter
): SelectionInput {
  const handler = createTableTabNavigationKeyDownHandler(adapter);
  let unsubscribe: (() => void) | undefined;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribeSyncKeyboardDown(
        (capture: KeyboardDownCapture) => {
          if (capture.properties.consumed) {
            return;
          }

          handler(capture, machine);

          if (capture.properties.consumed) {
            capture.properties.event.stopPropagation?.();
          }
        }
      );
    },

    dispose() {
      unsubscribe?.();
    }
  };
}
